import { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";

interface EditableFieldProps {
  label: string;
  value: string | number | null;
  type?: "text" | "number" | "select" | "enum";
  options?: { value: number | string; label: string }[];
  onSave: (value: string | number | null) => Promise<void>;
  formatDisplay?: (value: string | number | null) => string;
  isSubmitting?: boolean;
}

const EditableField = ({
  label,
  value,
  type = "text",
  options,
  onSave,
  formatDisplay,
  isSubmitting = false,
}: EditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>(value?.toString() || "");
  const [isSaving, setIsSaving] = useState(false);

  // Update editValue when value prop changes (after save)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value?.toString() || "");
    }
  }, [value, isEditing]);

  const handleEdit = () => {
    setEditValue(value?.toString() || "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(value?.toString() || "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    let valueToSave: string | number | null = editValue;
    
    if (type === "number") {
      valueToSave = editValue === "" ? null : (isNaN(parseFloat(editValue)) ? null : parseFloat(editValue));
    } else if (type === "enum" || type === "select") {
      // For select/enum, check if the value is a string or number
      if (editValue === "") {
        valueToSave = null;
      } else {
        // Check if the option value is a string or number
        const option = options?.find(opt => opt.value.toString() === editValue);
        if (option) {
          // Use the original value type from options (string or number)
          valueToSave = option.value;
        } else {
          // Fallback: try to parse as number, otherwise use as string
          valueToSave = isNaN(parseInt(editValue)) ? editValue : parseInt(editValue);
        }
      }
    } else if (editValue === "") {
      valueToSave = null;
    }
    
    try {
      await onSave(valueToSave);
      setIsEditing(false);
    } catch (ex) {
      console.log(ex);
    } finally {
      setIsSaving(false);
    }
  };

  const displayValue = formatDisplay ? formatDisplay(value) : (value?.toString() || "Not specified");

  if (isEditing) {
    return (
      <div className="d-flex align-items-center gap-2">
        <div className="flex-grow-1">
          {type === "select" || type === "enum" ? (
            <Form.Select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              size="sm"
            >
              <option value="">Not specified</option>
              {options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          ) : type === "number" ? (
            <Form.Control
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              size="sm"
            />
          ) : (
            <Form.Control
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              size="sm"
            />
          )}
        </div>
        <Button
          variant="success"
          size="sm"
          onClick={handleSave}
          disabled={isSaving || isSubmitting}
        >
          ✓
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCancel}
          disabled={isSaving || isSubmitting}
        >
          ×
        </Button>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2">
      <span className="flex-grow-1">{displayValue}</span>
      <Button
        variant="link"
        size="sm"
        onClick={handleEdit}
        style={{ padding: "0.125rem 0.25rem", textDecoration: "none" }}
        title="Edit"
      >
        ✏️
      </Button>
    </div>
  );
};

export default EditableField;

